import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";

/**
 * Tracks unread training announcements for the current staff user.
 *
 * Returns:
 *   unreadCount: number (0 when all caught up)
 *   markSeen(): void — call when user opens the Training Manual tab
 *
 * Polls every 90s so announcements posted by an admin during a staff
 * session surface without requiring a page reload.
 */
export default function useTrainingUnread(enabled = true) {
  const [unreadCount, setUnreadCount] = useState(0);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled || inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await API.get("/training/announcements/unread-count");
      setUnreadCount(res.data?.unread_count || 0);
    } catch {
      // Silent — training is a nice-to-have; don't spam the console
    } finally {
      inFlight.current = false;
    }
  }, [enabled]);

  const markSeen = useCallback(async () => {
    try {
      await API.post("/training/announcements/mark-seen");
      setUnreadCount(0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const id = setInterval(refresh, 90_000);
    return () => clearInterval(id);
  }, [enabled, refresh]);

  return { unreadCount, markSeen, refresh };
}
